import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { showSymbolWarnings } from "@/components/notice";
import { StateBadge } from "@/components/session";
import { useIndexSymbols } from "@/components/symbols";
import { Button, DecimalField, ErrorNote, Field, SidePanel, inputClass, useCurrentUser } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { keys, useRefreshDirectory, useServers } from "@/lib/queries";
import { decimalInput } from "@/lib/units";
import type { Account, SessionListItem } from "@/lib/types";

/*
 * LE FASI BROKER CONTRO BROKER (BB) NELL'INTERFACCIA. Funzione su misura.
 *
 * Quello che serve per FARE una BB (nascita, sezione della scheda cliente,
 * scheda BB della pagina Operazione) compare solo se `/auth/me` dice
 * `capabilities.brokerPairs`: `useBrokerPairsActive`, l'unica lettura. Quello
 * che riguarda una BB gia' nata (dettaglio, etichette, rapporto) si decide sulla
 * Fase (`kind`), come nel backend: spenta la funzione, le BB aperte si chiudono.
 * Decide sempre il backend; qui si mostra o si nasconde.
 */

/** Il tenant ha le BB attive (`/auth/me`). Senza, nessun elemento BB. */
export function useBrokerPairsActive(): boolean {
  return useCurrentUser().capabilities?.brokerPairs === true;
}

export const isBrokerPair = (s: { kind?: string }): boolean => s.kind === "BB";

/** Le etichette delle due gambe: sulla BB la gamba PROP e' il conto A, la BROKER il conto B. */
export const BB_LEG_LABELS = { PROP: "Broker A", BROKER: "Broker B" } as const;

/** Il rapporto come si legge: virgola e due decimali almeno («1,00», «0,5» -> «0,50»). */
export const ratioText = (r: string | null): string =>
  r === null ? "—" : Number(r).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 4 });

/** Il rapporto scritto nel campo: maggiore di zero, fino a 10, al massimo quattro decimali (come il backend). */
function ratioIssue(text: string): string | null {
  const v = decimalInput(text);
  if (v === null) return "Scrivi il rapporto di copertura, per esempio 1,00";
  if (!(Number(v) > 0) || Number(v) > 10) return "Il rapporto di copertura va da 0 escluso a 10";
  if ((v.split(".")[1] ?? "").length > 4) return "Il rapporto di copertura ha al massimo quattro decimali";
  return null;
}

// ---------------------------------------------------------------------------
// La nascita: dal menu del conto broker, con questo conto come A
// ---------------------------------------------------------------------------

export function NewBrokerPairPanel({
  account,
  clientId,
  clientName,
  brokerAccounts,
  onClose,
}: {
  /** Il conto A: la gamba di partenza. */
  account: Account;
  clientId: string;
  clientName: string;
  /** I conti broker LIBERI del cliente (`freeBrokerAccounts`): fra questi, tolto A, si sceglie B. */
  brokerAccounts: Account[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const refresh = useRefreshDirectory();
  const servers = useServers();
  const indici = useIndexSymbols();
  const candidatiB = brokerAccounts.filter((a) => a.id !== account.id);
  /** "" = B dopo: la Fase nasce da completare. */
  const [accountBId, setAccountBId] = useState("");
  const [symbol, setSymbol] = useState("");
  const [ratio, setRatio] = useState("1,00");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Il simbolo: XAUUSD, e gli indici dove sono attivi; mappato sul server di A e su quello di B se c'e'.
  const serverA = servers.data?.find((s) => s.id === account.serverId);
  const serverB = accountBId ? servers.data?.find((s) => s.id === candidatiB.find((a) => a.id === accountBId)?.serverId) : undefined;
  const ammessi = ["XAUUSD", ...indici];
  const symbolOptions = !serverA
    ? []
    : ammessi.filter((c) => serverA.symbols.some((m) => m.canonical === c) && (!serverB || serverB.symbols.some((m) => m.canonical === c)));
  const effSymbol = symbolOptions.includes(symbol) ? symbol : "";
  const ratioProblem = ratioIssue(ratio);
  const problems = [!effSymbol && "simbolo", ratioProblem && "rapporto di copertura"].filter(Boolean) as string[];

  const submit = async () => {
    if (problems.length) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.sessions.createBrokerPair({
        clientId,
        accountAId: account.id,
        ...(accountBId && { accountBId }),
        canonicalSymbol: effSymbol,
        hedgeRatio: decimalInput(ratio)!,
      });
      showSymbolWarnings(r);
      await refresh();
      navigate(`/fasi/${r.session.id}`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open
      onClose={onClose}
      title="Nuova Fase BB"
      subtitle={`${clientName} · broker A ${account.label} · ${account.mt5Login}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Annulla
          </Button>
          <Button variant="primary" disabled={busy || problems.length > 0} onClick={() => void submit()} title={problems.length ? `Manca: ${problems.join(", ")}` : undefined}>
            {busy ? "Creazione…" : "Crea Fase BB"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted">
          Broker contro broker: il broker A apre nella direzione scelta, il broker B in quella opposta. Niente ciclo, drawdown, target né proxy.
        </p>
        <Field label="Broker B" hint={!candidatiB.length ? "Nessun altro conto broker libero del cliente: la Fase nasce da completare, e B si aggiunge dopo." : undefined}>
          {(id) => (
            <select id={id} value={accountBId} onChange={(e) => setAccountBId(e.target.value)} className={inputClass}>
              <option value="">Dopo (la Fase nasce da completare)</option>
              {candidatiB.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label} · {a.mt5Login}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Simbolo" hint={serverA && !symbolOptions.length ? "Nessuno strumento della BB mappato sui server dei due conti: mappalo dalla pagina Server." : undefined}>
          {(id) => (
            <select id={id} value={effSymbol} onChange={(e) => setSymbol(e.target.value)} className={inputClass}>
              <option value="" disabled>
                Scegli il simbolo
              </option>
              {symbolOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          )}
        </Field>
        <DecimalField
          label="Rapporto di copertura"
          value={ratio}
          onChange={setRatio}
          hint="Lotti B = lotti A × rapporto × contratto A / contratto B, al passo di B più vicino. Si cambia dopo dalla Fase."
        />
        {ratioProblem && decimalInput(ratio) !== null && <p className="text-xs text-loss">{ratioProblem}</p>}
        <ErrorNote>{error}</ErrorNote>
      </div>
    </SidePanel>
  );
}

// ---------------------------------------------------------------------------
// Il rapporto di copertura di una BB
// ---------------------------------------------------------------------------

export function HedgeRatioEditor({ s, canWrite }: { s: { id: string; hedgeRatio: string | null; state: string }; canWrite: boolean }) {
  const refresh = useRefreshDirectory();
  const [ratio, setRatio] = useState(ratioText(s.hedgeRatio));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const editable = canWrite && (s.state === "ACTIVE" || s.state === "PAUSED" || s.state === "AWAITING_BROKER");
  const problem = ratioIssue(ratio);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    if (problem) return setError(problem);
    setBusy(true);
    try {
      await api.sessions.setHedgeRatio(s.id, decimalInput(ratio)!);
      await refresh();
      setSaved(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-3">
      <h2 className="text-sm font-medium text-ink">Rapporto di copertura</h2>
      <p className="text-sm text-muted">
        Lotti B = lotti A × rapporto × contratto A / contratto B, al passo di B più vicino. Vale per le operazioni che apri da adesso: quelle già aperte tengono il loro.
      </p>
      {editable ? (
        <div className="max-w-xs">
          <DecimalField label="Rapporto" value={ratio} onChange={setRatio} />
        </div>
      ) : (
        <p className="text-sm tabular-nums text-ink">{ratioText(s.hedgeRatio)}</p>
      )}
      <ErrorNote>{error}</ErrorNote>
      {editable && (
        <div className="flex items-center gap-3">
          <Button variant="primary" type="submit" disabled={busy}>
            {busy ? "Salvataggio…" : "Salva il rapporto"}
          </Button>
          {saved && <span className="text-sm text-gain">Salvato.</span>}
        </div>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------
// «Da completare»: manca il broker B
// ---------------------------------------------------------------------------

export function AwaitingBrokerBNotice({ canWrite, onAttach }: { canWrite: boolean; onAttach: () => void }) {
  return (
    <div className="rounded-card border border-warn/40 bg-warn/5 px-4 py-3 text-sm">
      <p className="text-ink">Fase da completare: manca il broker B</p>
      <p className="mt-1 text-muted">Finché il broker B non c'è non si apre nessuna operazione. Scegli un conto broker libero dello stesso cliente, o creane uno.</p>
      {canWrite && (
        <Button size="sm" className="mt-2" onClick={onAttach}>
          Aggiungi broker B
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scheda cliente: le Fasi BB del cliente
// ---------------------------------------------------------------------------

const NON_CONCLUSE = ["ACTIVE", "PAUSED", "AWAITING_BROKER"];

/** Le Fasi BB non concluse del cliente. Si monta solo con la capacita' attiva: senza, nessuna richiesta. */
export function BrokerPairsSection({ clientId, canWrite, onAttach }: { clientId: string; canWrite: boolean; onAttach: (sessionId: string) => void }) {
  const q = { clientId, state: NON_CONCLUSE };
  const sessions = useQuery({ queryKey: keys.sessionList(q), queryFn: () => api.sessions.list(q) });
  const bb = (sessions.data ?? []).filter(isBrokerPair);
  if (!bb.length) return null;
  return (
    <section>
      <h2 className="mb-2 text-sm font-medium text-ink">Fasi broker contro broker</h2>
      <div className="grid gap-4 md:grid-cols-2">
        {bb.map((s) => (
          <BrokerPairCard key={s.id} s={s} canWrite={canWrite} onAttach={() => onAttach(s.id)} />
        ))}
      </div>
    </section>
  );
}

function BrokerPairCard({ s, canWrite, onAttach }: { s: SessionListItem; canWrite: boolean; onAttach: () => void }) {
  const daCompletare = s.state === "AWAITING_BROKER";
  return (
    <div className="card space-y-3 p-4 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-ink">{s.canonicalSymbol} · rapporto {ratioText(s.hedgeRatio)}</span>
        {daCompletare ? <span className="rounded border border-warn/40 px-1.5 py-0.5 text-xs text-warn">Da completare</span> : <StateBadge kind="session" state={s.state} />}
      </div>
      <div className="grid grid-cols-2 gap-x-3 text-xs">
        <div className="min-w-0">
          <p className="text-faint">{BB_LEG_LABELS.PROP}</p>
          <p className="truncate font-medium text-prop">{s.propAccount.label}</p>
          <p className="machine-id truncate">{s.propAccount.mt5Login}</p>
        </div>
        <div className="min-w-0">
          <p className="text-faint">{BB_LEG_LABELS.BROKER}</p>
          <p className="truncate font-medium text-broker">{s.brokerAccount?.label ?? "da aggiungere"}</p>
          <p className="machine-id truncate">{s.brokerAccount?.mt5Login ?? "—"}</p>
        </div>
      </div>
      {daCompletare && <AwaitingBrokerBNotice canWrite={canWrite} onAttach={onAttach} />}
      <Link to={`/fasi/${s.id}`} className="inline-block text-muted hover:text-ink hover:underline">
        Dettaglio della fase
      </Link>
    </div>
  );
}
